import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import cx from 'classnames';
import times from 'lodash/times';
import { searchPageSize, useSearch } from '@contexts/search-context';
import { useCheckInTooSoonReport } from '@hooks';
import { formatDateYYYYMMDD } from '@utils/date-helpers';
import { firstBookableCheckInDate, isDatedSearchTooSoon } from '@utils/booking-lead-time';
import { routes } from '@routes';
import { scrollToTop } from '@utils/scroll-to-top';
import { AnalyticsRef } from '@components/analytics-ref';
import { Button } from '@components/button';
import { Divider } from '@components/divider';
import { Link } from '@components/link';
import { ListingCard } from '@components/listing-card';
import { Typography } from '@components/typography';
import { ListingPartial } from './listing-map-card';
import { CheckInTooSoonNotice } from '@components/check-in-too-soon-notice';
import { LowInventoryNotice } from './low-inventory-notice';
import { SKELETON_COUNT, PRIORITY_IMAGE_THRESHOLD } from './constants';

// listing_engine caps the search count at this value (total_count_limit in
// list_all_active_for_region and the bounding box read), so a count landing on
// the cap means "at least this many" and cannot claim to be over it. A live
// US-wide search returns exactly 1000.
const SEARCH_COUNT_CAP = 1000;

// Only called behind a positive count; the zero case is the empty state's, and
// it renders NO RESULTS rather than a count.
function propertyCountFormattedString(count: number): string {
  if (count === 1) return '1 stay';
  if (count < SEARCH_COUNT_CAP) return `${count} stays`;
  return `${SEARCH_COUNT_CAP.toLocaleString('en-US')}+ stays`;
}

/**
 * The one empty state a settled search can be in. Modelled rather than left as
 * three disjoint JSX conditionals so precedence is structural: a check-in
 * inside the platform booking lead time explains the zero result, and the
 * other two would misattribute it.
 */
type SearchEmptyState = {
  kind: 'check-in-too-soon';
  firstBookableOn: string;
} | {
  kind: 'international';
} | {
  kind: 'no-results';
};
interface SearchResultsListProps {
  resultsContainerRef: React.RefObject<HTMLDivElement | null>;
  isListActive: boolean;
  shouldShowContent: boolean;
  shouldShowLoadingState: boolean;
  searchedCountry: string | undefined;
  hasFiltersApplied: boolean;
  isAboveMinZoom: boolean;
  zoomingOutToNewListing: boolean;
  selectedListing: ListingPartial | undefined;
  onHoverListing: (slug: string | undefined) => void;
  onSaveScrollPosition: () => void;
  onClearDates: () => void;
  onResetFilters: () => void;
  onZoomOut: () => void;
  onFavoritedSuccess: (listing: ListingPartial, isNowFavorited: boolean) => void;
}
export const SearchResultsList: React.FC<SearchResultsListProps> = t0 => {
  const $ = _c(43);
  const {
    resultsContainerRef,
    isListActive,
    shouldShowContent,
    shouldShowLoadingState,
    searchedCountry,
    hasFiltersApplied,
    isAboveMinZoom,
    zoomingOutToNewListing,
    selectedListing,
    onHoverListing,
    onSaveScrollPosition,
    onClearDates,
    onResetFilters,
    onZoomOut,
    onFavoritedSuccess
  } = t0;
  const {
    checkInDate,
    checkOutDate,
    adultCount,
    childCount,
    infantCount,
    petCount,
    listingsLoading,
    listingsQueryResults,
    nextPage,
    prevPage
  } = useSearch();
  const entries = listingsQueryResults?.listingsSearch.__typename === "ListingSearchCursorCollection" ? listingsQueryResults.listingsSearch.entries : [];
  const metadata = listingsQueryResults?.listingsSearch.__typename === "ListingSearchCursorCollection" ? listingsQueryResults.listingsSearch.metadata : null;
  const resultCount = metadata?.totalCount ?? 0;
  let t1;
  bb0: {
    const isReady = isListActive && shouldShowContent && !listingsLoading && !zoomingOutToNewListing && entries.length === 0;
    if (!isReady) {
      t1 = null;
      break bb0;
    }
    if (isDatedSearchTooSoon(checkInDate, checkOutDate)) {
      let t2;
      if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = {
          kind: "check-in-too-soon",
          firstBookableOn: firstBookableCheckInDate()
        };
        $[0] = t2;
      } else {
        t2 = $[0];
      }
      t1 = t2;
      break bb0;
    }
    if (searchedCountry === "US") {
      let t2;
      if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = {
          kind: "no-results"
        };
        $[1] = t2;
      } else {
        t2 = $[1];
      }
      t1 = t2;
      break bb0;
    }
    if (searchedCountry) {
      let t2;
      if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = {
          kind: "international"
        };
        $[2] = t2;
      } else {
        t2 = $[2];
      }
      t1 = t2;
      break bb0;
    }
    t1 = null;
  }
  const emptyState = t1;
  let t2;
  if ($[3] !== checkInDate || $[4] !== checkOutDate || $[5] !== emptyState) {
    t2 = emptyState?.kind === "check-in-too-soon" && checkInDate ? {
      checkInDate,
      checkOutDate,
      firstBookableCheckInDate: emptyState.firstBookableOn,
      surface: "search"
    } : null;
    $[3] = checkInDate;
    $[4] = checkOutDate;
    $[5] = emptyState;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  useCheckInTooSoonReport(t2);
  if (!isListActive) {
    return null;
  }
  const t3 = !isListActive;
  let t4;
  if ($[7] !== t3) {
    t4 = cx("block h-full w-full lg:w-2/3 overflow-hidden max-w-5xl px-4 lg:px-2 mt-16 lg:mt-0", {
      hidden: t3
    });
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const t5 = "w-full h-full overflow-hidden";
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="relative lg:hidden"><Divider /></div>;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== emptyState || $[11] !== hasFiltersApplied || $[12] !== onClearDates || $[13] !== onResetFilters || $[14] !== onZoomOut) {
    t7 = emptyState?.kind === "check-in-too-soon" && <><CheckInTooSoonNotice firstBookableOn={emptyState.firstBookableOn} className="pl-4 sm:pl-8 pt-16 sm:py-16 w-full text-gray-dark" /><div className="pl-4 sm:pl-8 w-full"><SearchActionButtons canZoomOut={false} hasDates={true} hasFiltersApplied={hasFiltersApplied} onZoomOut={onZoomOut} onClearDates={onClearDates} onResetFilters={onResetFilters} /></div></>;
    $[10] = emptyState;
    $[11] = hasFiltersApplied;
    $[12] = onClearDates;
    $[13] = onResetFilters;
    $[14] = onZoomOut;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== checkInDate || $[17] !== checkOutDate || $[18] !== emptyState?.kind || $[19] !== hasFiltersApplied) {
    t8 = emptyState?.kind === "international" && <LowInventoryNotice variant="zero-international" totalCount={0} hasFiltersApplied={hasFiltersApplied} hasDates={!!(checkInDate || checkOutDate)} />;
    $[16] = checkInDate;
    $[17] = checkOutDate;
    $[18] = emptyState?.kind;
    $[19] = hasFiltersApplied;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== checkInDate || $[22] !== checkOutDate || $[23] !== emptyState?.kind || $[24] !== hasFiltersApplied || $[25] !== isAboveMinZoom || $[26] !== onClearDates || $[27] !== onResetFilters || $[28] !== onZoomOut) {
    t9 = emptyState?.kind === "no-results" && <><LowInventoryNotice variant="zero" totalCount={0} hasFiltersApplied={hasFiltersApplied} hasDates={!!(checkInDate || checkOutDate)} /><div className="pl-4 sm:pl-8 w-full"><SearchActionButtons canZoomOut={isAboveMinZoom} hasDates={!!(checkInDate || checkOutDate)} hasFiltersApplied={hasFiltersApplied} onZoomOut={onZoomOut} onClearDates={onClearDates} onResetFilters={onResetFilters} /></div></>;
    $[21] = checkInDate;
    $[22] = checkOutDate;
    $[23] = emptyState?.kind;
    $[24] = hasFiltersApplied;
    $[25] = isAboveMinZoom;
    $[26] = onClearDates;
    $[27] = onResetFilters;
    $[28] = onZoomOut;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] !== t7 || $[31] !== t8 || $[32] !== t9) {
    t10 = <div className="h-full w-full mt-1 pt-2 lg:mt-0 lg:pt-0 px-2 z-50">{t6}{t7}{t8}{t9}</div>;
    $[30] = t7;
    $[31] = t8;
    $[32] = t9;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  let t11;
  if ($[34] !== shouldShowLoadingState) {
    t11 = shouldShowLoadingState && <div className="relative inset-0 min-h-screen sm:min-h-[calc(100vh-80px)] overflow-auto"><div className="hidden lg:flex mx-4 mt-5 h-4 bg-gray-xlight rounded w-36" /><div className="absolute w-full h-min grid grid-col-1 xs:grid-cols-2 xl:grid-cols-3 pt-4 p-2">{times(SKELETON_COUNT, _temp)}</div></div>;
    $[34] = shouldShowLoadingState;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  const t12 = shouldShowContent && entries.length > 0 && metadata && <div ref={resultsContainerRef} style={{
    zIndex: 1,
    visibility: shouldShowContent ? "visible" : "hidden",
    opacity: shouldShowContent ? 1 : 0,
    transition: "opacity 0.1s ease-in-out"
  }} className="relative inset-0 min-h-[calc(100vh-72px)] lg:min-h-[calc(100vh-81px)] overflow-auto bg-white">{resultCount > 0 && <Typography variant="subtitle2" variantM="subtitle2" component="div" className="hidden lg:flex px-4 pt-[18px] flex-row items-center relative inset-0 overflow-auto text-primary-main">{listingsLoading ? <div data-id="result-count-placeholder" className="animate-pulse h-4 bg-gray-light rounded w-48" /> : <><h5 className="pl-1">{propertyCountFormattedString(resultCount)}</h5><Link to={routes.about} target="_blank" rel="noreferrer" className="pl-1">without the markup</Link></>}</Typography>}<AnalyticsRef name="search_results" data-page-size={searchPageSize} data-testid="@search/search-results"><div className="absolute top-0 lg:top-10 w-full"><LowInventoryNotice variant="low" totalCount={resultCount} hasFiltersApplied={hasFiltersApplied} hasDates={!!(checkInDate || checkOutDate)} /><div className="grid grid-cols-1 px-4 sm:grid-cols-2 sm:px-2 xl:grid-cols-3 gap-2 sm:gap-2">{entries.filter(_temp2).map((listing, index_0) => <div key={listing.slug} className="mb-6 sm:mb-0" onMouseEnter={() => onHoverListing(listing.slug)} onMouseLeave={() => onHoverListing(undefined)} onClickCapture={onSaveScrollPosition}><ListingCard listing={listing} altTag="temp" accountShowRoute={routes.accounts(listing.account.slug || "")} avatarUrl={listing.account.avatarUrl} isFavorited={listing.isFavorited} displayStay={!checkInDate && !checkOutDate} showRoute={routes.listings({
              slug: listing.slug,
              checkInDate: checkInDate ? formatDateYYYYMMDD(checkInDate) : listing.priceDisplay?.checkInDate ? formatDateYYYYMMDD(listing.priceDisplay.checkInDate) : undefined,
              checkOutDate: checkOutDate ? formatDateYYYYMMDD(checkOutDate) : listing.priceDisplay?.checkOutDate ? formatDateYYYYMMDD(listing.priceDisplay.checkOutDate) : undefined,
              adultCount,
              childCount,
              infantCount,
              petCount
            })} priority={index_0 <= PRIORITY_IMAGE_THRESHOLD} onFavoritedSuccess={isNowFavorited => {
              if (selectedListing && selectedListing.id === listing.id) {
                onFavoritedSuccess(selectedListing, isNowFavorited);
              }
            }} /></div>)}</div>{resultCount > searchPageSize && <div className="relative bottom-0 block bg-white h-40"><nav className="flex absolute bottom-24 lg:bottom-14 w-full text-center justify-center space-x-4" aria-label="Search results pagination"><div className={cx({
              "cursor-not-allowed": !metadata.cursorBefore
            })}><Button onClick={() => {
                prevPage();
                scrollToTop();
              }} variant="outlined" color="gray" size="sm" aria-label="Previous page" disabled={!metadata.cursorBefore}>Previous</Button></div><div className={cx({
              "cursor-not-allowed": !metadata.cursorAfter
            })}><Button onClick={() => {
                nextPage();
                scrollToTop();
              }} variant="outlined" color="gray" size="sm" aria-label="Next page" disabled={!metadata.cursorAfter}>Next</Button></div></nav></div>}{resultCount <= searchPageSize && isAboveMinZoom && <div className="block sm:pl-8 pb-40 sm:pb-16 w-full text-gray-dark"><div className="text-sm text-center sm:text-left font-medium pt-4">There may be more results. You can try</div><SearchActionButtons canZoomOut={isAboveMinZoom} hasDates={!!(checkInDate || checkOutDate)} hasFiltersApplied={hasFiltersApplied} onZoomOut={onZoomOut} onClearDates={onClearDates} onResetFilters={onResetFilters} size="sm" /></div>}</div></AnalyticsRef></div>;
  let t13;
  if ($[36] !== t10 || $[37] !== t11 || $[38] !== t12) {
    t13 = <div className={t5}>{t10}{t11}{t12}</div>;
    $[36] = t10;
    $[37] = t11;
    $[38] = t12;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  let t14;
  if ($[40] !== t13 || $[41] !== t4) {
    t14 = <div className={t4}>{t13}</div>;
    $[40] = t13;
    $[41] = t4;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  return t14;
};
interface SearchActionButtonsProps {
  /**
   * Whether zooming out could help. The map's zoom level decides that
   * normally; the too-soon empty state passes false outright, because
   * widening the map cannot help when the exclusion is platform wide and
   * driven by the dates.
   */
  canZoomOut: boolean;
  hasDates: boolean;
  hasFiltersApplied: boolean;
  onZoomOut: () => void;
  onClearDates: () => void;
  onResetFilters: () => void;
  size?: 'sm' | 'md' | 'lg';
}
const SearchActionButtons: React.FC<SearchActionButtonsProps> = t0 => {
  const $ = _c(16);
  const {
    canZoomOut,
    hasDates,
    hasFiltersApplied,
    onZoomOut,
    onClearDates,
    onResetFilters,
    size
  } = t0;
  let t1;
  if ($[0] !== canZoomOut || $[1] !== onZoomOut || $[2] !== size) {
    t1 = canZoomOut && <Button variant="outlined" color="gray" className="text-gray-dark border-[1.5px] outline-gray-dark" onClick={onZoomOut} size={size}>Show nearby properties</Button>;
    $[0] = canZoomOut;
    $[1] = onZoomOut;
    $[2] = size;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== hasDates || $[5] !== onClearDates || $[6] !== size) {
    t2 = hasDates && <Button variant="outlined" color="gray" className="text-gray-dark border-[1.5px] outline-gray-dark" onClick={onClearDates} size={size}>Clear Dates</Button>;
    $[4] = hasDates;
    $[5] = onClearDates;
    $[6] = size;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== hasFiltersApplied || $[9] !== onResetFilters || $[10] !== size) {
    t3 = hasFiltersApplied && <Button variant="outlined" className="text-gray-dark border-[1.5px] outline-gray-dark" color="gray" onClick={onResetFilters} size={size}>Reset Filters</Button>;
    $[8] = hasFiltersApplied;
    $[9] = onResetFilters;
    $[10] = size;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== t1 || $[13] !== t2 || $[14] !== t3) {
    t4 = <div className="mt-6 items-center space-y-4 flex flex-col xs:flex-row xs:space-x-4 xs:space-y-0">{t1}{t2}{t3}</div>;
    $[12] = t1;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  return t4;
};
function _temp(index) {
  return <div key={index} className="animate-pulse flex space-x-4 w-full px-2"><div className="py-2 space-y-2 flex flex-col w-full"><div className="relative h-64 sm:h-full aspect-[100/95] rounded-xl bg-gray-light" /><div className="space-y-2 py-1"><div className="h-4 bg-gray-xlight rounded w-36" /><div className="h-2 bg-gray-xlight rounded w-20" /><div className="h-2 bg-gray-xlight rounded w-24" /><div className="hidden sm:block h-4 bg-gray-xlight rounded w-48" /><div className="block sm:hidden h-4 bg-gray-xlight rounded w-72" /></div></div></div>;
}
function _temp2(l) {
  return l.slug;
}